import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Components
import useToast from '../../components/molecules/Toast/useToast';

// Queries
import {
  useGlobalFiltersMutation,
  GLOBAL_FILTERS_QUERY_KEY,
  globalFiltersMutationKey,
} from './globalFiltersQueries';
import { useManagerGenericMutation } from '../genericUrl/genericUrlQueries';
import { postManagerGeneric } from '../genericUrl/genericUrlService';
import { TABLE_FILTERS_QUERY_KEY } from '../table-filters/tableFiltersQueries';
import { TABLE_QUERY_KEY } from '../table/tableQueries';
import {
  TIMELINE_FILTERS_QUERY_KEY,
  TIMELINE_QUERY_KEY,
} from '../calendar/calendarQueries';

/**
 * Custom hook for optimistic updates of manager global filters filter values
 * @param {string} path - The page path for the query key
 * @param {import('@tanstack/react-query').UseMutationOptions<ApiManagerGlobalFiltersCreate, Error, ApiManagerGlobalFiltersCreate, unknown>} [options = {}] - The mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<ApiManagerGlobalFiltersCreate, Error, ApiManagerGlobalFiltersCreate, unknown>} The mutation with optimistic updates
 */
export const useGlobalFiltersOptimistic = (path, options = {}) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  const {
    onSuccess, onError, onSettled, ...restOptions
  } = options;

  return useGlobalFiltersMutation(path, {
    ...restOptions,
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: [GLOBAL_FILTERS_QUERY_KEY, path] });

      /** @type {ApiManagerGlobalFilters} */
      const old = queryClient.getQueryData([GLOBAL_FILTERS_QUERY_KEY, path]);

      const optimisticManagerGlobalFilters = {
        ...old,
        dateRange: {
          ...old?.dateRange,
          type: data?.dateRange?.type || old?.dateRange?.type,
          rangeStartDate: data?.dateRange?.rangeStartDate || old?.dateRange?.rangeStartDate,
          rangeEndDate: data?.dateRange?.rangeEndDate || old?.dateRange?.rangeEndDate,
          selectedDate: data?.dateRange?.selectedDate || old?.dateRange?.selectedDate,
        },
        personalViews: {
          ...old?.personalViews,
          selectedOption: (() => {
            const value = data?.personalViews?.selectedOption ?? old?.personalViews?.selectedOption;
            return value === '' ? null : value;
          })(),
        },
      };

      queryClient.setQueryData(
        [GLOBAL_FILTERS_QUERY_KEY, path],
        () => ({ ...optimisticManagerGlobalFilters }),
      );

      return { optimisticManagerGlobalFilters };
    },
    onError: (
      /** @type {Error} */ error,
      /** @type {ApiManagerGlobalFiltersCreate} */ variables,
    ) => {
      notify({
        message: error?.message || t('toast.globalFilters.error'),
        type: 'alert',
      });
      onError?.(error, variables);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [GLOBAL_FILTERS_QUERY_KEY, path] });
      // Invalidate table queries when global filters change to trigger refetch
      queryClient.invalidateQueries({ queryKey: [TABLE_QUERY_KEY], type: 'active' });
      // Table-filters may depend on global scope; partial key covers all contexte values.
      queryClient.invalidateQueries({ queryKey: [TABLE_FILTERS_QUERY_KEY, path] });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_QUERY_KEY], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [TIMELINE_FILTERS_QUERY_KEY, path] });
      onSettled?.(data, error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
    mutationKey: globalFiltersMutationKey(path),
  });
};

/**
 * @typedef {object} PersonalViewsOptimisticVariables
 * @property {Record<string, unknown>} [requestBody] - Payload sent to the API
 * @property {Partial<ApiManagerPersonalViews>} [optimisticPersonalViewsPatch] - Cache patch for personalViews
 */

/**
 * Custom hook for optimistic updates of manager personal views (global filters or table filters)
 * @param {string} mutationKey - The mutation key to use for the optimistic update
 * @param {string} url - The URL to call
 * @param {string} path - The page path for the query key
 * @returns {import('@tanstack/react-query').UseMutationResult<unknown, Error, PersonalViewsOptimisticVariables, unknown>} The mutation with optimistic updates
 */
export const usePersonalViewsOptimistic = (mutationKey, url, path) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { t } = useTranslation();

  return useManagerGenericMutation(url, {
    mutationFn: (variables) => postManagerGeneric(url, variables?.requestBody ?? {}),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: [mutationKey, path] });

      queryClient.setQueryData(
        [mutationKey, path],
        (/** @type {ApiManagerGlobalFilters | ApiManagerTableFilters} */ old) => ({
          ...old,
          personalViews: {
            ...old?.personalViews,
            ...variables?.optimisticPersonalViewsPatch,
          },
        }),
      );

      return { optimisticPersonalViewsPatch: variables?.optimisticPersonalViewsPatch };
    },
    onError: (
      /** @type {Error} */ error,
    ) => {
      notify({
        message: error?.message || t('toast.personalViews.error'),
        type: 'alert',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [mutationKey, path] });
    },
  });
};
